import { visibleFallback } from "@/lib/cms/cms-new-i18n";

/**
 * Clés et fallbacks FR pour les messages structurés renvoyés par l'API d'import
 * (`code` + `params`). Les textes définitifs vivent dans `GlobalI18n`.
 */

export const IMPORT_WARNING_KEY_PREFIX = "bo.import.server.warning.";

export const IMPORT_WARNING_FALLBACKS: Record<string, string> = {
  templateFromAnotherInstance:
    "Ce modèle a été téléchargé depuis une autre instance. Les colonnes seront rapprochées d'après leur intitulé.",
  templateForAnotherTable:
    "Ce modèle a été généré pour « {{metaTable}} », alors que vous importez « {{table}} ».",
  noDataRows:
    "Votre fichier ne contient aucune ligne de données : seuls les en-têtes ont été trouvés. Remplissez une ligne par fiche à partir de la ligne 4, puis déposez-le à nouveau.",
  duplicateKeysWithinFile:
    "{{count}} numéro(s) d'enregistrement apparaît/apparaissent sur plusieurs lignes de votre fichier.",
  missingRequiredColumn: "Colonne obligatoire absente : {{labels}}.",
  tooManyDecisions:
    "Votre fichier demande plus de {{max}} arbitrages de liaison. Vérifiez la correspondance de vos colonnes, ou découpez le fichier.",
  tooManySheets:
    "Le classeur contient plus de {{max}} feuilles, les suivantes sont ignorées.",
  emptySheet: "Aucune feuille de données exploitable n'a été trouvée.",
  multipleSheets:
    "Le classeur contient plusieurs feuilles. « {{sheet}} » a été retenue.",
  missingHeaderRow:
    "Aucune ligne d'en-têtes n'a été trouvée dans les {{scanRows}} premières lignes. La première ligne de votre fichier doit contenir le nom des colonnes.",
  tooManyColumns: "Plus de {{max}} colonnes : les suivantes sont ignorées.",
  headerRowShifted:
    "Les noms de colonnes ont été trouvés à la ligne {{row}}. Les {{skipped}} première(s) ligne(s) ont été ignorée(s).",
  tooManyRows:
    "Le fichier dépasse {{max}} lignes. Découpez-le en plusieurs fichiers.",
  duplicateHeaders: "Colonnes en double : {{headers}}.",
};

export const IMPORT_DISCLAIMER_FALLBACKS: Record<string, string> = {
  "bo.import.server.disclaimer.nothingWritten":
    "Rien n'a encore été écrit : cette analyse ne modifie aucune fiche.",
  "bo.import.server.disclaimer.emptyCellSafe":
    "Une cellule vide ne supprimera jamais une information déjà enregistrée.",
  "bo.import.server.disclaimer.canUndo":
    "Un import peut être annulé depuis son rapport : les fiches créées seront supprimées. Les fiches mises à jour, elles, ne pourront pas être rétablies.",
  "bo.import.server.disclaimer.approxLinks":
    "Les valeurs de liaison approximatives vous seront soumises avant tout enregistrement.",
  "bo.import.server.disclaimer.stripFormatting":
    "La mise en forme des textes collés depuis un traitement de texte sera retirée.",
};

export const IMPORT_HIGHLIGHT_FALLBACKS: Record<string, string> = {
  "bo.import.server.highlight.topValue":
    "{{count}} ligne(s) sur {{total}} ({{pct}} %) ont « {{value}} » comme {{label}}",
  "bo.import.server.highlight.topFilledField":
    "{{label}} est renseigné sur {{filled}} ligne(s) sur {{total}} ({{pct}} %)",
};

export const IMPORT_ERROR_KEY_PREFIX = "bo.import.server.error.";

export const IMPORT_ERROR_FALLBACKS: Record<string, string> = {
  importNotFound: "Import introuvable.",
  tooManyConcurrentImports:
    "{{count}} imports sont déjà en cours pour votre instance (maximum {{max}}). Terminez-les ou annulez-les avant d'en lancer un autre.",
  jobAlreadyFinished: "Cet import est déjà terminé.",
};

export const IMPORT_DECISION_KEY_PREFIX = "bo.import.server.decision.";

export const IMPORT_DECISION_FALLBACKS: Record<string, string> = {
  vocabularyKept:
    "« {{value}} » sera conservé tel quel et ajouté à votre liste « {{label}} ».",
  noMatch: "« {{value}} » ({{label}}) ne correspond à aucune fiche existante.",
  reasonAlias: "résolu via un choix précédent : « {{value}} » → {{label}}",
  reasonExact: "correspondance exacte avec {{label}}",
  reasonExactNormalized:
    "correspond à {{label}} (différences de casse ou d'accents seulement)",
  reasonTokenSet: "correspond à {{label}} (ordre des mots inversé)",
  reasonInitials: "correspond à {{label}} (initiale développée)",
  reasonFuzzy: "correspond à {{label}} ({{percent}})",
  ambiguous:
    "« {{value}} » ressemble autant à {{topLabel}} ({{topPercent}}) qu'à {{secondLabel}} ({{secondPercent}}). Lequel ?",
  resembles:
    "« {{value}} » ressemble à {{label}} ({{percent}}). Est-ce la même fiche ?",
};

type TranslateFn = (
  key: string,
  options?: Record<string, unknown>,
) => string;

function interpolate(
  template: string,
  params: Record<string, string | number> = {},
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) =>
    params[key] != null ? String(params[key]) : "",
  );
}

/** Traduit un warning d'analyse (`code` + `params`, ou ancien `message`). */
export function translateImportWarning(
  t: TranslateFn,
  warning: {
    code: string;
    message?: string;
    params?: Record<string, string | number>;
  },
): string {
  // Toujours préférer le code quand on le connaît : les jobs mis en cache
  // avant le basculement i18n portent encore un `message` figé (souvent EN)
  // alors que l'UI est en FR.
  const knownFallback = IMPORT_WARNING_FALLBACKS[warning.code];
  if (!knownFallback) {
    return warning.message ?? warning.code;
  }

  const key = `${IMPORT_WARNING_KEY_PREFIX}${warning.code}`;
  const params = warning.params ?? {};
  const fallbackText = interpolate(knownFallback, params);

  const translated = t(key, {
    ...params,
    defaultValue: visibleFallback(key, fallbackText),
  });

  // Si i18next renvoie la clé brute (bundle pas encore chargé), appliquer le
  // fallback interpolé sans le marqueur — sinon on affiche la clé seule.
  if (translated === key) return fallbackText;
  return translated;
}

/**
 * Libellé de champ dans la langue d'interface.
 *
 * Les clés Prisma (`oeuvresDescriptionNumeroEnregistrement`, …) sont aussi des
 * clés `GlobalI18n` : on les résout ici pour ne pas afficher un label EN figé
 * dans un rapport analysé dans une autre langue.
 */
export function translateImportFieldLabel(
  t: TranslateFn,
  fieldKey: string | null | undefined,
  fallbackLabel: string,
): string {
  if (!fieldKey) return fallbackLabel;
  const translated = t(fieldKey, { defaultValue: fallbackLabel });
  return translated === fieldKey ? fallbackLabel : translated;
}

export function translateImportDisclaimer(t: TranslateFn, key: string): string {
  // Anciens jobs : disclaimer déjà localisé en texte libre.
  if (!key.startsWith("bo.import.")) return key;

  const fallback = IMPORT_DISCLAIMER_FALLBACKS[key] ?? key;
  const translated = t(key, {
    defaultValue: visibleFallback(key, fallback),
  });
  return translated === key ? fallback : translated;
}

export function translateImportHighlight(
  t: TranslateFn,
  highlight: {
    messageKey?: string;
    kind: string;
    fieldKey?: string | null;
    label: string;
    value: string | null;
    count: number;
    total?: number;
    pct?: number;
    share?: number;
    message?: string;
  },
): string {
  const key =
    highlight.messageKey ??
    (highlight.kind === "topFilledField"
      ? "bo.import.server.highlight.topFilledField"
      : "bo.import.server.highlight.topValue");

  // Ancien job sans messageKey : si on ne connaît pas le gabarit, garder
  // le message serveur ; sinon recomposer dans la langue UI.
  if (highlight.message && !highlight.messageKey && !IMPORT_HIGHLIGHT_FALLBACKS[key]) {
    return highlight.message;
  }

  const total = highlight.total ?? 0;
  const pct =
    highlight.pct ??
    (highlight.share != null ? Math.round(highlight.share * 100) : 0);
  const label = translateImportFieldLabel(
    t,
    highlight.fieldKey,
    highlight.label ?? "",
  );
  const params = {
    count: highlight.count,
    filled: highlight.count,
    total,
    pct,
    value: highlight.value ?? "",
    label: label.toLowerCase(),
  };
  const fallback = IMPORT_HIGHLIGHT_FALLBACKS[key] ?? key;
  const translated = t(key, {
    ...params,
    defaultValue: visibleFallback(key, interpolate(fallback, params)),
  });
  return translated === key ? interpolate(fallback, params) : translated;
}

export function translateImportDecisionExplanation(
  t: TranslateFn,
  code: string,
  params: Record<string, string | number> = {},
): string {
  const key = `${IMPORT_DECISION_KEY_PREFIX}${code}`;
  const fallback = IMPORT_DECISION_FALLBACKS[code] ?? code;
  const translated = t(key, {
    ...params,
    defaultValue: visibleFallback(key, interpolate(fallback, params)),
  });
  return translated === key ? interpolate(fallback, params) : translated;
}

/**
 * Erreur API d'import (`code` + `params`, ou ancien `message` figé / clé i18n).
 */
export function translateImportApiError(
  t: TranslateFn,
  error: {
    code?: string;
    message?: string;
    params?: Record<string, string | number>;
  },
  fallback: string,
): string {
  const code = error.code;
  if (code && IMPORT_ERROR_FALLBACKS[code]) {
    const key = `${IMPORT_ERROR_KEY_PREFIX}${code}`;
    const params = error.params ?? {};
    const fallbackText = interpolate(IMPORT_ERROR_FALLBACKS[code], params);
    const translated = t(key, {
      ...params,
      defaultValue: visibleFallback(key, fallbackText),
    });
    return translated === key ? fallbackText : translated;
  }

  // Ancienne réponse : `message` = clé `bo.import.server.error.*`
  if (error.message?.startsWith("bo.import.server.error.")) {
    const key = error.message;
    const short = key.slice(IMPORT_ERROR_KEY_PREFIX.length);
    const params = error.params ?? {};
    const fallbackText = IMPORT_ERROR_FALLBACKS[short]
      ? interpolate(IMPORT_ERROR_FALLBACKS[short], params)
      : fallback;
    const translated = t(key, {
      ...params,
      defaultValue: visibleFallback(key, fallbackText),
    });
    return translated === key ? fallbackText : translated;
  }

  return error.message ?? fallback;
}
